/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link, useParams } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import ExportButton from "../active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "../active-users/DateRangeBar";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import SubscriptionHistorySummaryCards from "./SubscriptionHistorySummaryCards";
import "../active-users/active-users.css";
import "./Subscription.css";

const LIMIT = 20;

const ANDROID_NOTIFICATION_TYPES = {
  1: 'SUBSCRIPTION_RECOVERED', 2: 'SUBSCRIPTION_RENEWED', 3: 'SUBSCRIPTION_CANCELED',
  4: 'SUBSCRIPTION_PURCHASED', 5: 'SUBSCRIPTION_ON_HOLD', 6: 'SUBSCRIPTION_IN_GRACE_PERIOD',
  7: 'SUBSCRIPTION_RESTARTED', 8: 'SUBSCRIPTION_PRICE_CHANGE_CONFIRMED', 9: 'SUBSCRIPTION_DEFERRED',
  10: 'SUBSCRIPTION_PAUSED', 11: 'SUBSCRIPTION_PAUSE_SCHEDULE_CHANGED', 12: 'SUBSCRIPTION_REVOKED',
  13: 'SUBSCRIPTION_EXPIRED', 20: 'SUBSCRIPTION_PENDING_PURCHASE_CANCELED',
};
const NOTIFICATION_TYPE_COLOURS = {
  1: { backgroundColor: '#dcfce7', color: '#166534' }, 2: { backgroundColor: '#dbeafe', color: '#1e40af' },
  3: { backgroundColor: '#fee2e2', color: '#991b1b' }, 4: { backgroundColor: '#d1fae5', color: '#065f46' },
  5: { backgroundColor: '#fef9c3', color: '#854d0e' }, 6: { backgroundColor: '#ffedd5', color: '#9a3412' },
  7: { backgroundColor: '#ccfbf1', color: '#115e59' }, 8: { backgroundColor: '#e0e7ff', color: '#3730a3' },
  9: { backgroundColor: '#f3e8ff', color: '#6b21a8' }, 10: { backgroundColor: '#fef3c7', color: '#92400e' },
  11: { backgroundColor: '#ecfccb', color: '#3f6212' }, 12: { backgroundColor: '#ffe4e6', color: '#9f1239' },
  13: { backgroundColor: '#f3f4f6', color: '#1f2937' }, 20: { backgroundColor: '#fce7f3', color: '#9d174d' },
};
const FALLBACK_COLOUR = { backgroundColor: '#f1f5f9', color: '#334155' };

// The Android code can arrive under either column name (production stores
// `notification_type`, other environments `notificationtype`) and as a number or a
// numeric string depending on the column type — read it defensively so a naming
// drift never degrades the whole column to "Unknown".
const notificationCode = (row) => {
  const raw = row.notificationtype != null ? row.notificationtype : row.notification_type;
  if (raw === null || raw === undefined || raw === '') return null;
  const code = Number(raw);
  return Number.isFinite(code) ? code : null;
};

const androidLabel = (code) => ({
  name: ANDROID_NOTIFICATION_TYPES[code] || `Unknown (code ${code})`,
  style: NOTIFICATION_TYPE_COLOURS[code] || FALLBACK_COLOUR,
});

const notificationLabel = (row) => {
  const code = notificationCode(row);
  const platform = Number(row.platform);
  if (platform === 1) {
    return code === null ? { name: 'Unknown', style: FALLBACK_COLOUR } : androidLabel(code);
  }
  if (platform === 2) return { name: row.purchasetype || 'Unknown', style: FALLBACK_COLOUR };
  // Platform missing/unexpected — fall back to whichever payload the row carries.
  if (code !== null) return androidLabel(code);
  return { name: row.purchasetype || 'Unknown', style: FALLBACK_COLOUR };
};

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'starttimemillis', label: 'Start Time' },
  { key: 'expirytimemillis', label: 'Expiry Time' },
  { key: 'auto_renew', label: 'Auto Renew' },
  { key: 'price', label: 'Price' },
  { key: 'countrycode', label: 'Country Code' },
  { key: 'order_id', label: 'Order ID' },
  { key: 'notification', label: 'Notification Type' },
  { key: 'cancelreason', label: 'Cancel Reason' },
  { key: 'datetime', label: 'Datetime' },
];

const History = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  let subId = '';
  try { subId = atob(id || ''); } catch { subId = ''; }

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [rows, setRows] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [summary, setSummary] = useState(null);
  const [subscriberName, setSubscriberName] = useState('');
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // No default date window — the detail page opens all-time; the user can
    // narrow with the date range bar.
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => {
    if (token) loadAll(1, fromDate, toDate, searchValue, platformFilter);
  }, [token]);

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });
  const listUrl = (p, f, t, s, pf) =>
    `${baseUrl}/admin/api/subscription-history?id=${subId}&page=${p}&limit=${LIMIT}&from_date=${f}&to_date=${t}&search=${s}&platform=${pf}`;
  const sumUrl = (f, t) =>
    `${baseUrl}/admin/api/subscription-history-summary?id=${subId}${f && t ? `&from_date=${f}&to_date=${t}` : ''}`;
  const handleErr = (err) => { setError(err?.response?.status === 401 ? 'auth' : 'load'); };

  const applyRows = (data) => {
    setRows(data.data || []); setTotalItem(data.totalItem || 0);
    if (data.data && data.data[0] && data.data[0].name) setSubscriberName(data.data[0].name);
  };

  const getList = async (p, f, t, s, pf) => {
    setLoading(true); setError('');
    try { applyRows((await axiosJWT.get(listUrl(p, f, t, s, pf), auth())).data); }
    catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const loadAll = async (p, f, t, s, pf) => {
    setLoading(true); setError('');
    try {
      const [list, sum] = await Promise.all([
        axiosJWT.get(listUrl(p, f, t, s, pf), auth()),
        axiosJWT.get(sumUrl(f, t), auth()),
      ]);
      applyRows(list.data);
      setSummary(sum.data && sum.data.success ? sum.data.data : null);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => { setPage(pageNumber); getList(pageNumber, fromDate, toDate, searchValue, platformFilter); };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key) => {
    const { fromStr, toStr } = presetRange(key, new Date());
    setPreset(key); setFromDate(fromStr); setToDate(toStr);
    setDisplayFromDate(fromStr ? new Date(fromStr) : null); setDisplayToDate(toStr ? new Date(toStr) : null);
    setPage(1); loadAll(1, fromStr, toStr, searchValue, platformFilter);
  };
  const handleSearch = () => { setPage(1); loadAll(1, fromDate, toDate, searchValue, platformFilter); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null); setPreset(null);
    setSearchValue(''); setPlatformFilter(''); setPage(1);
    loadAll(1, '', '', '', '');
  };

  const formatDateTime = (s) => {
    if (!s || s === '-') return '-';
    const [datePart, timePart] = String(s).split(' ');
    if (!datePart) return '-';
    const [y, m, d] = datePart.split('-');
    return `${d}/${m}/${y}${timePart ? ` ${timePart}` : ''}`;
  };
  const dateFormatter = (cDate) => {
    if (!cDate) return '-';
    const d = new Date(cDate);
    const p = (x) => String(x).padStart(2, '0');
    return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`;
  };
  // Apple stores the price in milliunits, Google Play in micros (same as the List page).
  const isIosRow = (row) => {
    const platform = Number(row.platform);
    if (platform === 2) return true;
    if (platform === 1) return false;
    return notificationCode(row) === null && !!row.purchasetype;
  };
  const price = (row) => {
    if (!row.priceamountmicros || !/^[0-9]+$/.test(String(row.priceamountmicros))) return '-';
    const divisor = isIosRow(row) ? 1000 : 1_000_000;
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: row.pricecurrencycode || 'USD' })
        .format(parseInt(row.priceamountmicros, 10) / divisor);
    } catch { return `${(parseInt(row.priceamountmicros, 10) / divisor).toFixed(2)} ${row.pricecurrencycode || ''}`; }
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/subscription-history-export?id=${subId}&from_date=${fromDate}&to_date=${toDate}&search=${searchValue}&platform=${platformFilter}`,
      auth());
    return (res.data.data || []).map((r) => ({
      ...r,
      auto_renew: r.auto_renewing === '1' || r.auto_renewing === 'true' ? 'Yes' : 'No',
      price: price(r),
      notification: notificationLabel(r).name,
      datetime: dateFormatter(r.created_at),
    }));
  };

  const hasFilters = !!(fromDate || toDate || searchValue || platformFilter);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Subscriptions › <b>History</b></span>
            <span className="au-route">/subscription/history</span>
            <span className="au-spacer" />
            <span className="au-back"><Link to="/user-subscription">← Back</Link></span>
          </div>
          <h1 className="au-title">
            Subscription History{subscriberName ? <span className="sub-delta__lbl"> — {subscriberName}</span> : ''}
          </h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the subscription history. Please try again." onRetry={() => loadAll(page, fromDate, toDate, searchValue, platformFilter)} />
          ) : (
            <>
              <SubscriptionHistorySummaryCards summary={summary} />

              <DateRangeBar
                leading={(
                  <>
                    <input className="au-input" type="text" placeholder="Search by name" value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)} aria-label="Search history" />
                    <select className="au-select" value={platformFilter} aria-label="Platform filter"
                      onChange={(e) => { setPlatformFilter(e.target.value); setPage(1); loadAll(1, fromDate, toDate, searchValue, e.target.value); }}>
                      <option value="">All Platforms</option>
                      <option value="Android">Android</option>
                      <option value="iOS">iOS</option>
                    </select>
                  </>
                )}
                preset={preset} displayFrom={displayFromDate} displayTo={displayToDate} hasRange={hasFilters}
                onPreset={handlePreset} onFromChange={handleFromChange} onToChange={handleToChange}
                onSearch={handleSearch} onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="sub-history-range-note">
                The date range filters events by their <b>start date (UTC)</b>.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all events (no date filter).'}
              </p>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : rows.length === 0 ? (
                <EmptyState title="No events in this range" hasFilters={hasFilters} onClear={clearFilter}
                  message={hasFilters ? 'No webhook events match the current filters.' : 'There are no webhook events for this subscription yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Webhook timeline</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="subscription-history.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table sub-table" data-testid="sub-history-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Plan</th><th>Start Time (UTC)</th><th>Expiry Time (UTC)</th>
                          <th>Auto Renew</th><th>Price</th><th>Country Code</th><th>Order ID</th>
                          <th>Notification Type</th><th>Cancel Reason</th><th>Datetime</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => {
                          const { name, style } = notificationLabel(row);
                          return (
                            <tr key={`${row.id}-${index}`}>
                              <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                              <td data-label="Name" className="au-ellipsis">{row.name}</td>
                              <td data-label="Plan">{row.code || '-'}</td>
                              <td data-label="Start Time (UTC)">{formatDateTime(row.starttimemillis)}</td>
                              <td data-label="Expiry Time (UTC)">{formatDateTime(row.expirytimemillis)}</td>
                              <td data-label="Auto Renew">{row.auto_renewing === '1' || row.auto_renewing === 'true' ? 'Yes' : 'No'}</td>
                              <td data-label="Price" className="tnum">{price(row)}</td>
                              <td data-label="Country Code">{row.countrycode || '-'}</td>
                              <td data-label="Order ID" className="au-ellipsis">{row.order_id}</td>
                              <td data-label="Notification Type"><span className="sub-pill" style={style}>{name}</span></td>
                              <td data-label="Cancel Reason" className="au-ellipsis">{row.cancelreason && row.cancelreason !== 'null' && row.cancelreason !== 'undefined' ? row.cancelreason : ''}</td>
                              <td data-label="Datetime">{dateFormatter(row.created_at)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default History;
